const post = {
  slug: "avoid-fake-payment-screenshots-instagram-sellers",
  title: "How to Avoid Fake UPI Payment Screenshots When Selling on Instagram",
  seoTitle: "Avoid Fake UPI Payment Screenshots",
  description:
    "A UPI screenshot is not proof of payment. Learn how to verify a payment in your own account before you ship, spot the common scams and reject a fake screenshot with confidence.",
  excerpt:
    "A screenshot is not proof of payment. Here is how to check a UPI payment the right way, which scams to watch for and what to do when a screenshot does not match.",
  category: "Payments",
  tags: ["Fake payment screenshot", "UPI payment verification", "Instagram seller safety"],
  date: "2026-10-21",
  cover: {
    src: "/blog-images/avoid-fake-payment-screenshots-instagram-sellers-cover.png",
    alt: "A phone showing an Instagram DM where a seller waits for a payment to clear before shipping",
  },
  blocks: [
    {
      type: "p",
      text:
        "If you sell through Instagram DMs and accept UPI, you have probably received this message: \u201CPayment done, please check\u201D, followed by a screenshot. Most of the time the payment is real. Sometimes it is not, which is why you should always check the money in your own account before you ship anything.",
    },
    {
      type: "p",
      text:
        "A screenshot only shows what is on the buyer\u2019s phone. It does not show what reached your account. This guide explains how to verify a payment properly, which scams are the most common and what to do when a screenshot does not match.",
    },

    { type: "h2", text: "Why a screenshot proves nothing" },
    {
      type: "p",
      text:
        "Anyone can edit a screenshot, record a payment that later fails or show a transaction that is still pending. Image editors and fake payment apps make it easy to produce a convincing \u201Csuccess\u201D screen in a minute. Even a genuine screenshot can mislead, because a UPI payment can show as processing on the buyer\u2019s phone and then fail.",
    },
    {
      type: "p",
      text:
        "The only reliable proof is the credit in your own account. What your bank app, your UPI app or your bank\u2019s SMS shows is what counts. Nothing the buyer sends you replaces it.",
    },

    { type: "h2", text: "How to verify a UPI payment properly" },
    {
      type: "ol",
      items: [
        "**Check your own account, not the screenshot.** Open your bank or UPI app and look for the credit in your transaction history.",
        "**Match the amount exactly.** A payment of \u20B9499 is not a payment of \u20B9449 or \u20B94.99. Read every digit.",
        "**Match the details.** Compare the transaction ID (UTR) on the screenshot with the one in your history, along with the time and the sender\u2019s name where it is shown.",
        "**Wait for a final status.** If your app shows pending or processing, do not ship yet. Payments in that state can still fail.",
        "**Mark the order as paid only after this.** Ship the parcel or send the download after you have seen the money, never before.",
      ],
    },
    {
      type: "p",
      text:
        "This takes about a minute per order. Because shipping is always manual, you get this chance to verify before every parcel goes out. If the screenshot does not match your account, you simply reject the payment and the order does not move forward.",
    },

    { type: "h2", text: "Common scams and how to spot them" },
    {
      type: "ul",
      items: [
        "**The edited screenshot.** Fonts that look slightly off, a transaction ID that matches nothing in your history or a time that does not fit. The fix is always the same: check your own account.",
        "**The \u201Cwrong amount\u201D trick.** A buyer says they paid too much by mistake and asks you to send the difference back. Never return money you have not seen in your account. If an overpayment really happened, the buyer can raise it with their own bank or UPI app.",
        "**The \u201Ccollect request\u201D trick.** A buyer sends you a request and says you must approve it to receive the money. You never enter your UPI PIN to receive a payment. Entering it sends money out of your account.",
        "**The pressure play.** \u201CI am in a hurry, please ship now, the money is coming.\u201D Urgency is a reason to slow down, not to speed up.",
        "**The pending payment.** A screenshot of a payment that is still in progress. Wait until it settles in your account.",
      ],
    },
    {
      type: "callout",
      tone: "peach",
      title: "One rule that prevents most losses",
      text:
        "No payment in your account, no shipment. Apply it to every order, including regular customers and people you know. A rule with no exceptions is easy to explain and hard to argue with.",
    },

    { type: "h2", text: "Make payments easier to check" },
    {
      type: "p",
      text:
        "A few habits make verification faster and make scams harder to pull off:",
    },
    {
      type: "ul",
      items: [
        "**Ask buyers to add the order number in the UPI note.** It lets you match a payment to an order instead of guessing by amount, which is hard when many customers pay the same price.",
        "**Record the transaction ID against each order.** A note in your sheet or dashboard gives you evidence if a dispute comes up later.",
        "**Do not ship on a promise.** If a payment is delayed, tell the buyer politely that the order goes out once the payment clears.",
        "**Say your payment rule before the buyer pays.** One clear sentence in your reply saves an awkward argument afterwards.",
      ],
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "A polite hold",
      caption: "Illustrative example. Use your own wording.",
      messages: [
        { from: "buyer", text: "Payment done. Please ship today, I have sent the screenshot." },
        { from: "shop", text: "Thank you! I can see your screenshot. I will confirm and ship as soon as the payment shows in my account. It usually takes a few minutes." },
        { from: "buyer", text: "OK" },
        { from: "time", text: "A few minutes later" },
        { from: "shop", text: "Payment received. Your order is confirmed and I will share tracking once it ships." },
      ],
    },

    { type: "h2", text: "Stop relying on screenshots altogether" },
    {
      type: "p",
      text:
        "The cleaner fix is to take payment in a way that confirms itself. When a customer pays through a payment gateway such as Razorpay, the gateway tells your system the moment the payment succeeds, so nobody has to look at a screenshot. In Checkout DM, a connected gateway confirms payments automatically. Manual payments by UPI, QR code or bank transfer are confirmed by you.",
    },
    {
      type: "p",
      text:
        "In Checkout DM, shipping is always manual and you verify the payment first. If a buyer attaches a wrong or fake UPI screenshot, you can reject it and the order does not go ahead.",
    },
    {
      type: "p",
      text:
        "Payments go straight to your own account, and Checkout DM does not take a commission on your orders. A gateway does charge its own fee, so compare that cost with the time you spend checking payments by hand. Our guide to [UPI versus a payment gateway](/blog/accept-payments-instagram-upi-vs-gateway) walks through that trade-off, and [taking payments on Instagram in India](/blog/take-payments-on-instagram-india) covers every option.",
    },

    { type: "h2", text: "What to do when a screenshot does not match" },
    {
      type: "p",
      text:
        "Stay calm and do not argue in the chat. Because you verify before shipping, you lose nothing by saying no. Work through these steps instead:",
    },
    {
      type: "ol",
      items: [
        "**Do not ship.** Nothing leaves your hands until the credit appears in your own account.",
        "**Reject the payment.** If the amount, UTR or status does not match, reject the screenshot so the order does not go ahead.",
        "**Ask for the transaction ID.** Tell the buyer calmly that the payment has not reached you and ask them to share the UTR or a fresh screenshot.",
        "**Save your evidence.** Keep the chat and the screenshot in case the buyer disputes it later.",
        "**Report repeated fraud.** If someone keeps trying, you can file a complaint on [the National Cyber Crime Reporting Portal](https://cybercrime.gov.in) or call the helpline 1930.",
      ],
    },
  ],
  related: [
    { to: "/razorpay-keys-guide", title: "Razorpay Keys Guide", description: "Connect a gateway so payments are confirmed automatically." },
    { to: "/status-guide", title: "Order Status Guide", description: "What each order status means in your dashboard." },
    { to: "/faq", title: "Frequently Asked Questions", description: "Answers on payments, plans, shipping and returns." },
  ],
  faqs: [
    {
      q: "Is a UPI payment screenshot proof of payment?",
      a: "No. A screenshot only shows what is on the buyer\u2019s phone, and it can be edited or may show a payment that later fails. The only reliable proof is the credit in your own bank or UPI app.",
    },
    {
      q: "How can I check whether a UPI payment is real?",
      a: "Open your own bank or UPI app and look for the credit. Match the amount, the transaction ID (UTR), the time and the sender\u2019s name, and wait until the status is final before you ship.",
    },
    {
      q: "Do I need to enter my UPI PIN to receive money?",
      a: "No. You never enter your UPI PIN to receive a payment. If someone asks you to approve a request or enter your PIN to receive money, it is a scam, because entering the PIN sends money out.",
    },
    {
      q: "What if a buyer uploads a fake UPI screenshot in Checkout DM?",
      a: "You can reject it. Shipping is manual, so you check the payment in your own bank or UPI app first, and only confirm the order and ship once the money has actually arrived.",
    },
    {
      q: "Does Checkout DM confirm payments automatically?",
      a: "With a connected payment gateway such as Razorpay, yes. Manual payments by UPI, QR code or bank transfer are confirmed by you before the order moves on.",
    },
  ],
};

export default post;